import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { addToast } from '@heroui/react';
import { onlineManager, useQueryClient } from '@tanstack/react-query';

import { useUIStore } from '../stores/uiStore';
import { logger } from '../utils/logger';

import { RefreshContext } from './refresh';

interface RefreshProviderProps {
  children: ReactNode;
  defaultInterval?: number;
}

// Subscribe to onlineManager for React Query's "online" status
function getOnlineSnapshot(): boolean {
  return onlineManager.isOnline();
}
function subscribeToOnline(callback: () => void): () => void {
  return onlineManager.subscribe(callback);
}

export function RefreshProvider({
  children,
  defaultInterval = 30000, // 30 seconds
}: RefreshProviderProps): ReactElement {
  const queryClient = useQueryClient();
  const { editingProjectIds, editingTaskId } = useUIStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [refreshInterval, setRefreshInterval] = useState(defaultInterval);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  // Track React Query's online status - DON'T refresh when offline
  const isOnline = useSyncExternalStore(subscribeToOnline, getOnlineSnapshot);

  // Use a ref instead of state to track the timer
  // This prevents the useEffect from re-running when the timer changes
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshData = useCallback(
    async (additionalQueryKeys: string[] = []) => {
      // Don't refresh when offline - prevents spam
      if (isRefreshing || !isOnline) {
        return;
      }

      setIsRefreshing(true);
      setRefreshError(null); // Clear any previous errors
      try {
        // Invalidate all relevant queries
        const defaultQueryKeys = ['projects', 'tasks'];
        const allQueryKeys = [...defaultQueryKeys, ...additionalQueryKeys];

        // Use Promise.all to handle all invalidations and catch any errors
        await Promise.all(
          allQueryKeys.map(key => queryClient.invalidateQueries({ queryKey: [key] })),
        );

        // Update last refreshed timestamp
        setLastRefreshed(new Date());
      } catch (error) {
        logger.error(
          { component: 'RefreshContext' },
          'Error refreshing data',
          error instanceof Error ? error : undefined,
          {
            error: error instanceof Error ? error.message : String(error),
          },
        );
        // Only show toast if we're still online (otherwise it's expected)
        if (isOnline) {
          addToast({
            color: 'danger',
            description: 'Failed to refresh data. Please try again later.',
          });
        }
        setRefreshError('Failed to refresh data');
      } finally {
        setIsRefreshing(false);
      }
    },
    [isRefreshing, isOnline, queryClient],
  );

  const toggleAutoRefresh = useCallback(() => {
    setIsAutoRefreshEnabled(prev => !prev);
  }, []);

  // Set up the interval timer when auto-refresh is enabled AND online
  useEffect(() => {
    // Ensure minimum refresh interval is reasonable (e.g., 5 seconds)
    const safeInterval = Math.max(5000, refreshInterval);

    // Clear existing timer if any
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Don't auto-refresh when:
    // - Editing a project or task
    // - API is offline (prevent spam)
    const isEditing = editingProjectIds.size > 0 || editingTaskId !== null;

    if (isAutoRefreshEnabled && !isEditing && isOnline) {
      timerRef.current = setInterval(() => {
        void refreshData();
      }, safeInterval);
    }

    // Return a cleanup function
    return (): void => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    refreshInterval,
    isAutoRefreshEnabled,
    refreshData,
    editingProjectIds,
    editingTaskId,
    isOnline,
  ]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      isRefreshing,
      lastRefreshed,
      refreshInterval,
      setRefreshInterval,
      refreshData,
      isAutoRefreshEnabled,
      toggleAutoRefresh,
      refreshError,
    }),
    [
      isRefreshing,
      lastRefreshed,
      refreshInterval,
      refreshData,
      isAutoRefreshEnabled,
      toggleAutoRefresh,
      refreshError,
    ],
  );

  return <RefreshContext.Provider value={contextValue}>{children}</RefreshContext.Provider>;
}
